// How people actually TYPE their questions (owner, 17 Oct 2026; research: fears2/people/phrasing.md) — a pure normaliser
// shared by the three AVRASTRO assistants (citizens, Pro, CRM) and twinned in PHP for the website's own section ranking
// (apps/website/plugin/vero-connect/includes/text-normalise.php — keep the two in step; tests/unit/text-normalise.test.ts
// compares them on the same inputs).
//
// People write without accents, misspell the i / e / o sounds, double or drop consonants, write Cypriot («εν να
// πληρώσουν;», «τζαι», «ίντα»), Greeklish («8a plirwsoun?», «ti kalyptei i asfaleia zwhs»), Russian with typos
// («страхофка», «скок») or in Latin letters («zaplatyat li»), and mix English product words into Greek. Three layers:
//   1. fold        lower case, accents / diaeresis off, final sigma unified, ё → е, й → и
//   2. normalise   per word: Greeklish → Greek (a few spellings when a letter is ambiguous: x = χ or ξ, h = η or χ,
//                  th = θ or τη…), Latin-letter Russian → Cyrillic, Russian typos fixed, Cypriot function words → standard
//                  Greek («εν να» → «θα», «εν θα» → «δεν θα»; a lone «εν» is KEPT — it can mean "is" or "not")
//   3. key         a phonetic key for matching only: ι-sounds (η υ ει οι υι) → ι, ω → ο, αι → ε, ου → ȣ, αυ/ευ (and
//                  av/af/ev/ef as typed) → αφ/εφ, ντ → δ, γκ/γγ → γ, φθ → φτ, χθ → χτ, ξ → κσ, ψ → πσ, ь ъ dropped, doubled letters single.
//                  The Greek and the Greeklish spellings of a word get the same key («πληρώσουν» = «plirwsoun»).
// Russian keys are marked «~» and a properly typed Cyrillic word matches on its plain first 5 letters only, as before the
// normaliser (pages carry both); Latin-letter or typo-fixed Russian words use the plain stem and the key. On the website
// the series hub (guides/questions) goes after an article with the same score.
// A question becomes groups of alternative keys (one group per meaningful word: a page matches the word when it has any
// of them); a page's text becomes a plain set of keys. Regular-expression checks (worry, prices, advice) can run on the
// keyed text of each spelling (keyedVariants) with patterns passed through keyRegex.
export type ScriptKind = 'greek' | 'cyrillic' | 'greeklish' | 'translit' | 'latin' | 'none';
type L3 = 'el' | 'en' | 'ru';

/** lower case, accents and diaeresis removed, final sigma folded, ё → е, й → и (pure) */
export const foldText = (s: string): string => String(s ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ς/g, 'σ').replace(/ё/g, 'е');

// --- the phonetic key ----------------------------------------------------------------------------------------------
/** the matching key of folded text: Greek sound groups and Russian soft / hard signs collapsed (word edges unchanged) */
export function keyText(folded: string): string {
  return String(folded ?? '')
    .replace(/ου/g, 'ȣ')
    .replace(/([αε])[υβφ]/g, '$1φ')
    .replace(/αι/g, 'ε').replace(/ει|οι|υι/g, 'ι').replace(/[ηυ]/g, 'ι').replace(/ω/g, 'ο')
    .replace(/ντ/g, 'δ').replace(/γκ|γγ/g, 'γ').replace(/φθ/g, 'φτ').replace(/χθ/g, 'χτ').replace(/ξ/g, 'κσ').replace(/ψ/g, 'πσ')
    .replace(/[ьъ]/g, '')
    .replace(/([Ͱ-ϿЀ-ӿȣ])\1+/gu, '$1');
}
const GREEK = /[Ͱ-Ͽ]/;
const CYR = /[Ѐ-ӿ]/;
/** the short key of one folded word: the first 5 letters of its phonetic key (Latin words: their first 5 letters) */
export function keyStem(word: string): string {
  if (GREEK.test(word)) return keyText(word).slice(0, 5);
  // Russian: a final vowel / soft sign off first (жизни = жизнь = жизн)
  // (marked «~», so a key never meets a plain Russian stem: properly typed Russian ranks on plain stems as before)
  if (CYR.test(word)) { const k = keyText(word); return '~' + (k.length > 4 ? k.replace(/[аеиоуыэюя]+$/, '') : k).slice(0, 5); }
  return word.slice(0, 5);
}
/** a regular expression written for folded standard text, made to match keyed text (only Greek / Cyrillic letters change) */
export const keyRegex = (r: RegExp): RegExp => new RegExp(keyText(r.source), r.flags);

// --- which script / language the question is in --------------------------------------------------------------------
const GL_WORDS = new Set(['ti', 'na', 'ta', 'tou', 'tis', 'kai', 'tzai', 'tjai', 'einai', 'ine', 'pws', 'pos', 'poso', 'posa', 'posi', 'poia', 'pia', 'poios', 'pios', 'gia', 'tha', '8a', 'den', 'mou', 'sou', 'mas', 'sas', 'apo', 'oti', 'exw', 'exo', 'exei', 'exi', 'ehi', 'thelw', 'thelo', 'thelei', 'mporw', 'mporo', 'boro', 'inta', 'kati', 'pio', 'otan', 'pote', 'giati', 'afou', 'alla', 'oxi', 'nai', 'sto', 'sti', 'stin', 'ston', 'sta', 'h', 'kanw', 'kano', 'mia', 'ena', 'enan', 'en']);
const GL_STEMS = /^(asfal|apat|zwh|zoi|ygei|igei|igia|ygia|ugei|kalyp|kalip|plir|plhr|apozi|dikaiou|dikeou|gesy|gesi|syntax|sintax|lefta|paidi|pedia|xreiaz|hriaz|xriaz|chriaz|akyr|akir|kser|xer|3er|pethan|pe8an|giatr|nosok|asthen|arrost|danei|danio|epidom|kypr|kipro|foro|ginet|kostiz|axiz|aksiz|thel|8el|mhn|mina|eksetas|exetas|anamon|ypoxre|katharo|akriv|ftin|fthn|pairn|perno|pernei|ksan|ksero|stamat|egkyo|egyo|gennis|karkin|zaxar|zahar|kapniz|oikogen|ikogen|symvol|simvol)/;
/** Greek word endings typed in Latin letters (πληρώνουν, χρειάζομαι, ασφάλεια, χρεοκοπήσει …) */
const GL_END = /(oun|ousa|mai|eia|isei|isi|osi|wsi|eis|ika|isa|iso|wsw|wnw|ousame|oume|ontai)$/;
const RU_WORDS = new Set(['li', 'kto', 'chto', 'shto', 'kak', 'kakie', 'kakaya', 'kakoy', 'skolko', 'skok', 'stoit', 'mozhno', 'nuzhno', 'nuzhna', 'nuzhen', 'esli', 'umru', 'umer', 'ya', 'menya', 'mne', 'moy', 'moya', 'eto', 'budet', 'est', 'net', 'dlya', 'ili', 'tolko', 'zachem', 'pochemu', 'gde', 'kogda', 'nado', 'vy', 'vas', 'mozhet', 'kto-to', 'kipr', 'kipre']);
const RU_STEMS = /^(strah|strakh|zaplat|vyplat|deng|den'g|polis|vnzh|russk|govor|otkaz|razvod|rabot|sem'|semy|zhizn|zdorov|medic|pensi|dorog|deshev|platit|oplat|pokryt|vernut)/;
const RU_SHAPE = /zh|kh|shch|sch|yat$|yut$|ovk|aya$|iy$|yy$|'/;
const EN_WORDS = new Set(['the', 'what', 'is', 'are', 'does', 'do', 'how', 'my', 'you', 'your', 'insurance', 'life', 'cover', 'covered', 'pay', 'will', 'can', 'should', 'if', 'when', 'and', 'or', 'of', 'for', 'in', 'it', 'policy', 'health', 'need', 'get', 'much', 'cost', 'claim', 'die', 'family', 'money', 'after', 'before', 'with', 'without', 'not', 'yes', 'this', 'that', 'would', 'could', 'many', 'who', 'why', 'where', 'which', 'have', 'has', 'be', 'really', 'they', 'their', 'insurer', 'worth', 'cheap', 'cheapest', 'pension', 'kids', 'children', 'wife', 'husband', 'am', 'was', 'there']);
/** the script of a question: Greek, Cyrillic, Greeklish, Latin-letter Russian, Latin (English) or none (pure) */
export function detectScript(text: string): ScriptKind {
  const f = foldText(text);
  const g = (f.match(/[Ͱ-Ͽ]/g) ?? []).length, c = (f.match(/[Ѐ-ӿ]/g) ?? []).length, l = (f.match(/[a-z]/g) ?? []).length;
  if (!g && !c && !l) return 'none';
  if (g && g >= c && g >= l) return 'greek';
  if (c && c >= l) return 'cyrillic';
  let gl = 0, ru = 0, en = 0;
  for (const w of f.match(/[a-z0-9'-]+/g) ?? []) {
    if (!/[a-z]/.test(w)) continue;
    if (EN_WORDS.has(w)) en += 1;
    if (GL_WORDS.has(w)) gl += 1;
    if (GL_STEMS.test(w)) gl += 1;
    if (/[a-z][83][a-z]|^[83][a-z]/.test(w)) gl += 1;
    if (/[^aeiouy]w$|^[^aeiouy]*w[^aeiouyhn]/.test(w)) gl += 0.5;
    if (GL_END.test(w)) gl += 1;
    if (/ete$|^(xr|ps|ks|mp|gk|tz|8)/.test(w)) gl += 0.5;
    if (RU_WORDS.has(w)) ru += 1;
    if (RU_STEMS.test(w)) ru += 1;
    if (RU_SHAPE.test(w)) ru += 0.5;
  }
  if (gl > 0 && gl > ru && gl > en) return 'greeklish';
  if (ru > 0 && ru > gl && ru > en) return 'translit';
  return 'latin';
}
/** the language to answer in: Greek / Greeklish → el, Cyrillic / Latin-letter Russian → ru, otherwise the page's (pure) */
export function answerLang(text: string, fallback: L3): L3 {
  const s = detectScript(text);
  return s === 'greek' || s === 'greeklish' ? 'el' : s === 'cyrillic' || s === 'translit' ? 'ru' : fallback;
}

// --- Greeklish → Greek ---------------------------------------------------------------------------------------------
// longest first; an entry with two spellings branches (the first is the more common reading)
const GL_RULES: [string, string[]][] = [
  ['th', ['θ', 'τη']], ['ch', ['χ']], ['kh', ['χ', 'κη']], ['ph', ['φ', 'πη']], ['ps', ['ψ']], ['ks', ['ξ']], ['sh', ['σχ', 'ση']], ['ou', ['ου']],
  ['mp', ['μπ']], ['nt', ['ντ']], ['gk', ['γκ']], ['gg', ['γγ']], ['tz', ['τζ']], ['tj', ['τζ']], ['ts', ['τσ']], ['ai', ['αι']], ['ei', ['ει', 'εη']], ['oi', ['οι', 'οη']],
  ['a', ['α']], ['b', ['μπ']], ['c', ['κ']], ['d', ['δ']], ['e', ['ε']], ['f', ['φ']], ['g', ['γ']], ['i', ['ι']], ['j', ['τζ']], ['k', ['κ']], ['l', ['λ']],
  ['m', ['μ']], ['n', ['ν']], ['o', ['ο']], ['p', ['π']], ['q', ['κ']], ['r', ['ρ']], ['s', ['σ']], ['t', ['τ']], ['u', ['ου', 'υ']], ['v', ['β']], ['w', ['ω']],
  ['y', ['υ']], ['z', ['ζ']], ['8', ['θ']], ['3', ['ξ']],
];
const XI_FIRST = /^(exet|exot|exwt|axiz|exag|exig|exhg|taxid|exod|exart|exasf|exyp|exip|proex|exoda)/;
const VOWEL = /[aeiouyw]/;
const MAX_FORMS = 6;
/** the Greek spellings of one Latin-letter Greeklish word, the most likely first (at most 6; pure) */
export function greeklishForms(word: string): string[] {
  const w = foldText(word);
  let forms = [''];
  for (let i = 0; i < w.length;) {
    let opts: string[] | null = null, len = 1;
    const ch = w[i]!;
    if (ch === 'h') {
      const prev = w[i - 1] ?? '', next = w[i + 1] ?? '';
      opts = w.length === 1 ? ['η'] : !prev || (VOWEL.test(prev) && VOWEL.test(next)) || next === 'r' ? ['χ', 'η'] : ['η', 'χ'];
    } else if (ch === 'x') {
      opts = XI_FIRST.test(w) ? ['ξ', 'χ'] : ['χ', 'ξ'];
    } else {
      for (const [k, v] of GL_RULES) if (w.startsWith(k, i)) { opts = v; len = k.length; break; }
    }
    if (!opts) opts = [ch];
    const next: string[] = [];
    for (const f of forms) for (const o of opts) if (next.length < MAX_FORMS * 4) next.push(f + o);
    forms = next;
    i += len;
  }
  // keep the order (all-first-choice first) and at most MAX_FORMS spellings with different keys
  const seen = new Set<string>(), out: string[] = [];
  for (const f of forms) { const k = keyText(f); if (!seen.has(k)) { seen.add(k); out.push(f); } if (out.length >= MAX_FORMS) break; }
  return out;
}

// --- Latin-letter Russian → Cyrillic ------------------------------------------------------------------------------
const RU_RULES: [string, string][] = [
  ['shch', 'щ'], ['sch', 'щ'], ['zh', 'ж'], ['kh', 'х'], ['ch', 'ч'], ['sh', 'ш'], ['ts', 'ц'], ['ya', 'я'], ['ja', 'я'], ['yu', 'ю'], ['ju', 'ю'], ['yo', 'е'], ['jo', 'е'], ['ye', 'е'],
  ['a', 'а'], ['b', 'б'], ['v', 'в'], ['g', 'г'], ['d', 'д'], ['e', 'е'], ['z', 'з'], ['i', 'и'], ['j', 'и'], ['k', 'к'], ['l', 'л'], ['m', 'м'], ['n', 'н'], ['o', 'о'],
  ['p', 'п'], ['r', 'р'], ['s', 'с'], ['t', 'т'], ['u', 'у'], ['f', 'ф'], ['h', 'х'], ['c', 'ц'], ['w', 'в'], ['x', 'кс'], ['q', 'к'], ["'", 'ь'],
];
/** one Latin-letter Russian word in Cyrillic (y after a vowel = й, folded to и; otherwise ы) (pure) */
export function translitToCyrillic(word: string): string {
  const w = foldText(word);
  let out = '';
  for (let i = 0; i < w.length;) {
    if (w[i] === 'y' && !['a', 'u', 'o', 'e'].includes(w[i + 1] ?? '')) { out += /[aeiouy]/.test(w[i - 1] ?? '') ? 'и' : 'ы'; i++; continue; }
    let hit = false;
    for (const [k, v] of RU_RULES) if (w.startsWith(k, i)) { out += v; i += k.length; hit = true; break; }
    if (!hit) { out += w[i]; i++; }
  }
  return out;
}

// --- dialect, typos, synonyms ------------------------------------------------------------------------------------
/** doubled letters single, for looking words up in the maps below */
const undouble = (w: string) => w.replace(/(\p{L})\1+/gu, '$1');
/** Cypriot words → standard Greek (phrasing.md §3; keys without doubled letters). A lone «εν» is not here on purpose. */
const DIALECT: Record<string, string> = {
  ιντα: 'τι', ινταλωσ: 'πωσ', τζαι: 'και', τζιαι: 'και', τζι: 'και', τζε: 'και', τζιε: 'και', ποσα: 'ποσα', ποσον: 'ποσο', ποτε: 'ποτε', τωρα: 'τωρα',
  εσιει: 'εχει', εσιεισ: 'εχεισ', εσιω: 'εχω', εσιουμε: 'εχουμε', εσιουν: 'εχουν', εσιετε: 'εχετε', σιερι: 'χερι',
  καμνω: 'κανω', καμω: 'κανω', καμνεισ: 'κανεισ', καμνει: 'κανει', καμνουμε: 'κανουμε', καμνουν: 'κανουν', εμεναν: 'εμενα', εσεναν: 'εσενα',
  δαμε: 'εδω', τζιαμε: 'εκει', καλυτερα: 'καλυτερα', πραματα: 'πραγματα', κυνηι: 'κυνηγι', ασφαλιτησ: 'ασφαλιστησ', γεσι: 'γεσυ',
};
/** folk words → the word the site uses, added as an alternative for matching (phrasing.md §4) */
const SYNONYM: Record<string, string> = { ζαχαρο: 'διαβητησ', μπαλονακι: 'αγγειοπλαστικη', χαρτια: 'εγγραφα', προβιδεντ: 'προνοιασ', provident: 'προνοιασ' };
/** Russian typos and colloquial spellings (phrasing.md §6; keys without doubled letters) */
const RU_FIX: Record<string, string> = { скок: 'сколько', скоко: 'сколько', щас: 'сеичас', ща: 'сеичас', ваще: 'вообще', гези: 'геси', гесы: 'геси', гесу: 'геси', гезы: 'геси' };
const fixRussian = (w: string) => {
  const t = w.replace(/([жш])ы/g, '$1и').replace(/^страхоф/, 'страхов').replace(/^страхав/, 'страхов');
  return RU_FIX[undouble(t)] ?? t;
};
const fixGreek = (w: string) => DIALECT[undouble(w)] ?? w;

// --- words of a question -----------------------------------------------------------------------------------------
/** the text prepared for tokens: folded; «мед.страховка» split; an apostrophe inside a Latin word dropped */
const prepare = (text: string) => foldText(text).replace(/мед\.?\s?страх/g, 'мед страх').replace(/([a-z])'([a-z])/g, '$1$2');
const TOKEN = /[\p{L}\p{N}]+/gu;
/** the normalised spellings of one folded token in a text of the given script, the most likely first (pure) */
export function wordForms(tok: string, script: ScriptKind): string[] {
  if (GREEK.test(tok)) return [fixGreek(tok)];
  if (CYR.test(tok)) return [fixRussian(tok)];
  if (!/[a-z]/.test(tok)) return [tok];
  if (script === 'greeklish' || script === 'greek') return greeklishForms(tok).map(fixGreek);
  if (script === 'translit') return [fixRussian(translitToCyrillic(tok))];
  return [tok];
}
/** the whole question in normalised words, one reading per spelling choice (index 0 = the most likely; pure) */
export function normaliseVariants(text: string): string[] {
  const f = prepare(text), script = detectScript(f);
  const out: string[] = [];
  for (let v = 0; v < 2; v++) {
    let s = f.replace(TOKEN, (tok) => {
      // Latin product words inside a Greek sentence stay as they are («long covid καλυπτεται;»)
      if (script === 'greek' && /[a-z]/.test(tok) && !/[83]/.test(tok)) return tok;
      const forms = wordForms(tok, script);
      return forms[Math.min(v, forms.length - 1)]!;
    });
    // Cypriot phrases: «εν να» = «θα»; «εν θα» = «δεν θα»; a lone «εν» stays («εν απάτη;» is it a scam / «εν ξέρω» I don't know)
    s = s.replace(/(^|[^\p{L}])εν να(?=[^\p{L}]|$)/gu, '$1θα').replace(/(^|[^\p{L}])εν θα(?=[^\p{L}]|$)/gu, '$1δεν θα');
    if (!out.includes(s)) out.push(s);
  }
  return out;
}
/** the most likely normalised reading of a question (pure) */
export const normaliseText = (text: string) => normaliseVariants(text)[0]!;
/** each normalised reading keyed (for keyRegex patterns), plus the plain fold (pure) */
export const keyedVariants = (text: string) => [...new Set(normaliseVariants(text).map(keyText))];

/** is this folded word a stop word (as written, or by key: «ine» = «ειναι»)? */
function stopWith(stop: ReadonlySet<string>): (w: string) => boolean {
  let keyed = KEYED_STOP.get(stop);
  if (!keyed) { keyed = new Set([...stop].map((s) => keyText(foldText(s)))); KEYED_STOP.set(stop, keyed); }
  const k = keyed;
  return (w: string) => stop.has(w) || ((GREEK.test(w) || CYR.test(w)) && k.has(keyText(w)));
}
const KEYED_STOP = new WeakMap<ReadonlySet<string>, Set<string>>();
const meaningful = (w: string) => w.length >= 3 && !/^\d+$/.test(w);

/**
 * A question as groups of alternative keys, one group per meaningful word (pure). A Latin word in a Greeklish / Greek
 * question keeps its own letters too (an English product word: «term», «claim»); a word any of whose readings is a stop
 * word is left out.
 */
export function queryKeyGroups(text: string, stop: ReadonlySet<string> = new Set()): string[][] {
  const f = prepare(text), script = detectScript(f), isStop = stopWith(stop);
  const groups: string[][] = [];
  const seen = new Set<string>();
  for (const tok of f.match(TOKEN) ?? []) {
    const forms = wordForms(tok, script);
    if (forms.some((w) => isStop(w)) || isStop(tok)) continue;
    const alts = new Set<string>();
    if (/[a-z]/.test(tok) && script !== 'latin' && meaningful(tok)) alts.add(tok.slice(0, 5));
    for (const w of forms) {
      if (!meaningful(w)) continue;
      // Russian: a word typed properly in Cyrillic keeps its plain first 5 letters only (ranks exactly as before the
      // normaliser); a word in Latin letters or with a fixed typo gets the plain stem and the key (жизни = жизнь)
      if (CYR.test(w)) { alts.add(w.slice(0, 5)); if (w !== tok) alts.add(keyStem(w)); continue; }
      alts.add(keyStem(w));
      const syn = SYNONYM[w];
      if (syn) alts.add(keyStem(syn));
    }
    if (!alts.size) continue;
    const id = [...alts].sort().join('|');
    if (seen.has(id)) continue;
    seen.add(id);
    groups.push([...alts]);
  }
  return groups;
}
/** a page's text as a set of keys (no transliteration: pages are written properly) (pure) */
export function docKeys(text: string, stop: ReadonlySet<string> = new Set()): Set<string> {
  const isStop = stopWith(stop), out = new Set<string>();
  for (const w of foldText(text).match(TOKEN) ?? []) {
    if (!meaningful(w) || isStop(w)) continue;
    out.add(keyStem(w));
    if (CYR.test(w)) out.add(w.slice(0, 5)); // Russian: the plain stem as well as the key
  }
  return out;
}
